import { createFileRoute, useParams, Outlet } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { Skeleton, Divider } from "@heroui/react";
import { Rx } from "@/types/rx";
import RxForm from "@/components/rx/RxForm";
import RxExport from "@/components/rx/RxExport";
import RxTable from "@/components/rx/RxTable";
import RxPlotTable from "@/components/rx/RxPlotTable";
import useRequest from "@/lib/request/useRequest";
import useAuth from "@/lib/auth/useAuth";
import { useTranslation } from "react-i18next";
import { FertilizerType } from "@/types/products";

export const Route = createFileRoute("/dashboard/fields/$fieldId/rx/$rxId/")({
    component: RouteComponent,
});

function RouteComponent() {
    const { t } = useTranslation();
    const { rxId } = useParams({
        from: "/dashboard/fields/$fieldId/rx/$rxId",
    });
    const { useGetRequest, useMutateRequest } = useRequest();
    const [rx, setRx] = useState<Rx | undefined>(undefined);
    const [activeIrrigationSystemId, setActiveIrrigationSystemId] = useState<
        number | null
    >(null);
    const getRx = useGetRequest<Rx>({
        url: `/prescriptions/${rxId}/`,
        enabled: !!rxId,
    });
    const updateRxRequest = useMutateRequest<Rx>({
        method: "put",
        url: `/prescriptions/${rxId}/`,
        errorMessage: t("error.error_updating_rx", { ns: "messages" }),
        onSuccess: (data) => {
            setRx(data);
        },
    });
    useEffect(() => {
        if (getRx.data) {
            setRx(getRx.data);
        }
    }, [getRx.data]);
    const { user } = useAuth();
    const [fertilizerType, setFertilizerType] = useState<
        FertilizerType | undefined
    >(undefined);

    return getRx.isLoading ? (
        <div className="flex flex-col w-full h-full gap-2 p-2 mt-3">
            <Skeleton className="h-full rounded-lg" />
        </div>
    ) : (
        <div className=" pb-80">
            <RxForm
                rx={rx}
                updateRxRequest={updateRxRequest}
                setFertilizerType={setFertilizerType}
                onActiveIrrigationSystemChange={setActiveIrrigationSystemId}
            />
            {rx && <RxExport rx={rx} user={user} />}
            {rx && (
                <RxTable
                    rx={rx}
                    fertilizerType={fertilizerType}
                    scopedIrrigationSystemId={activeIrrigationSystemId}
                    onUpdate={(updatedRx: Rx) => {
                        setRx(updatedRx);
                    }}
                />
            )}
            <Divider className="my-2" />
            {!!rx && (
                <RxPlotTable
                    plots={rx.plots}
                    rxType={rx.rx_type}
                    onUpdate={() => {
                        getRx.refetch();
                    }}
                />
            )}
            <Outlet />
        </div>
    );
}
